<template lang="pug">
.frame(@keypress.enter.stop.prevent="doValidate" ref="frame")
  transition(
    name="frame-change",
    appear,
    mode="out-in",
    @before-leave="beforeLeave"
    @after-enter="afterEnter"
  )
    .frame__inner(:key="$route.params.slug")
      component(
        v-for="(field, i) in fields",
        :key="field.id || `${field.type}-${i}-${$route.params.slug}`",
        :is="field.type.toLowerCase()",
        :field="field"
      )
        template(#default="{ is, sub }")
          component(:is="is", :field="sub")
            template(#default="{ is, sub }")
              component(:is="is", :field="sub")
                template(#default="{ is, sub }")
                  component(:is="is", :field="sub")
                    template(#default="{ is, sub }")
                      component(:is="is", :field="sub")

      Footer(:formControls="formControls", :key="'footer'", :pushToDataLayer="pushToDataLayer")
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useForm } from "vee-validate";

import FieldTitle from "../display/FieldTitle.vue";
import Links from "../display/Links.vue";
import Note from "../display/Note.vue";
import Reflection from "../display/Reflection.vue";
import SectionHeader from "../display/SectionHeader.vue";
import SubHeader from "../display/SubHeader.vue";
import Separator from "../display/Separator.vue";
import Tooltip from "../display/Tooltip.vue";
import Row from "../display/Row.vue";

import Checkbox from "../fields/Checkbox";
import Currency from "../fields/Currency.vue";
import DateTime from "../fields/DateTime.vue";
import Dependency from "../fields/Dependency.vue";
import Dropdown from "../fields/Dropdown.vue";
import Duration from "../fields/Duration.vue";
import Email from "../fields/Email.vue";
import FileDrop from "../fields/FileDrop.vue";
import FullAddress from "../fields/FullAddress.vue";
import Integer from "../fields/Integer.vue";
import Name from "../fields/Name.vue";
import FullName from "../fields/FullName.vue";
import Phone from "../fields/Phone.vue";
import PlainText from "../fields/PlainText.vue";
import Radio from "../fields/Radio.vue";
import Range from "../fields/Range.vue";
import Repeatable from "../fields/Repeatable.vue";

import Footer from "./Footer.vue";

export default {
  name: "Frame",
  props: ["pushToDataLayer"],
  components: {
    fieldtitle: FieldTitle,
    links: Links,
    note: Note,
    sectionheader: SectionHeader,
    subheader: SubHeader,
    reflection: Reflection,
    separator: Separator,
    tooltip: Tooltip,
    row: Row,

    checkbox: Checkbox,
    currency: Currency,
    datetime: DateTime,
    dependency: Dependency,
    dropdown: Dropdown,
    duration: Duration,
    email: Email,
    filedrop: FileDrop,
    fulladdress: FullAddress,
    integer: Integer,
    name: Name,
    fullname: FullName,
    phone: Phone,
    plaintext: PlainText,
    radio: Radio,
    range: Range,
    repeatable: Repeatable,

    Footer,
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const { setTouched, validate } = useForm();
    const formControls = { setTouched, validate };
    const frame = ref(null);

    const fields = computed(() => store.getters.page(route.params.slug).fields);

    return { formControls, fields, doValidate, beforeLeave, afterEnter, frame };

    async function doValidate() {
      const { valid, errors } = await validate();
      const record = {};

      Object.keys(errors).forEach((field) => (record[field] = true));
      setTouched(record);

      if (valid) {
        const next = store.getters.nextPage(route.params.slug);
        router.push(next.slug);
        store.commit("setFormCompletionIndex", next.index);
      }
    }


    function beforeLeave(el) {
      frame.value.style.minHeight = `${el.offsetHeight}px`;
    }
    function afterEnter() {
      frame.value.style.minHeight = '';
    }
  },
};
</script>

<style scoped lang="scss">
.frame {
  display: flex;
  flex-direction: column;
  background: $white;
  padding-top: 1.5rem;
}

.frame__inner {
  display: flex;
  flex-direction: column;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  &.frame-change-enter-active,
  &.frame-change-leave-active {
    transition-duration: 0.5s;
    transition-timing-function: ease;
    transition-property: opacity;
  }
  &.frame-change-enter-from {
    opacity: 0;
    position: absolute;
  }
  &.frame-change-leave-to {
    opacity: 0;
    z-index: 1;
  }
}

::v-deep() {
  $scope: &;
  @at-root {
    label {
      @include label($scope: $scope);
    }
    input[type="text"],
    input[type="email"],
    input[type="number"],
    input[type="date"],
    input[type="password"],
    input[type="datetime-local"],
    input[type="tel"],
    .dropdown__display {
      @include textInput($scope: $scope);
    }
    input[type="radio"] {
      @include radioInput($scope: $scope);
    }
    input[type="checkbox"] {
      @include checkInput($scope: $scope);
    }
  }
}
</style>
